/**
 * Bucket (b) Amplitude product event names — PATHWAY-4096.
 *
 * Target: top-level event types (no UserAction envelope). Apps may still wrap
 * specific events during migration; see packages/fe-shared/src/amplitude/README.md.
 */
export const AmplitudeProductEvent = {
  // Auth
  LOGIN_SUCCESS: 'login_success',

  // Settings / appearance
  COLOR_MODE: 'color_mode',

  // Explore / search
  CATEGORY_CLICK: 'category_click',
  SUBCATEGORY_CLICK: 'subcategory_click',
  SEARCH_SUGGESTION_CLICKED: 'search_suggestion_clicked',
  SEARCH_RESULT: 'search_result',
  SEARCH_RESULT_CLICK: 'search_result_click',
  SEARCH_TIME_SPENT: 'search_time_spent',

  // Content
  RESOURCE_VIEW: 'resource_view',
  CONTENT_ISSUE: 'content_issue',
  DOCUMENT_PRINT: 'document_print',
  RESOURCE_PRINT: 'resource_print',
  CERTIFICATE_PRINT: 'certificate_print',
  REPORT_PRINT: 'report_print',
  DOCUMENT_SHARE: 'document_share',
  DOCUMENT_SHARE_DISMISSED: 'document_share_dismissed',
  DOCUMENT_SWITCH_LANGUAGE: 'document_switch_language',
  DOCUMENT_OPTIONS: 'document_options',
  DOCUMENT_OPTIONS_CANCEL: 'document_options_cancel',
  RESOURCE_SCREENSHOT: 'resource_screenshot',

  // Text (incl. WebView → native bridge)
  TEXT_COPY: 'text_copy',
  TEXT_PASTE: 'text_paste',
  TEXT_SELECTION: 'text_selection',

  // Smart search
  SMART_SEARCH_TOGGLE: 'smart_search.toggle',
  SMART_SEARCH_EMBEDDED_REFERENCE_TAPPED:
    'smart_search.embedded_reference_tapped',
  SMART_SEARCH_REFERENCE_IMAGE_OPENED: 'smart_search.reference_image_opened',
  SMART_SEARCH_REFERENCE_PREVIEW_CLOSED:
    'smart_search.reference_preview_closed',
  SMART_SEARCH_REFERENCE_SOURCE_CARD_TAPPED:
    'smart_search.reference_source_card_tapped',

  // Compliance (native)
  AGE_VERIFICATION_SCREEN_VIEWED: 'age_verification_screen_viewed',
  AGE_VERIFICATION_BLOCKED: 'age_verification_blocked',

  // Training plans / quizzes (web)
  QUIZ_CREATED: 'quiz_created',
  QUIZ_UPDATED: 'quiz_updated',
  TRAINING_PLAN_CREATED: 'training_plan_created',
  TRAINING_TEMPLATE_COPIED: 'training_template_copied',
  TRANSLATE_ALL: 'translate_all',
  TRANSLATE_SINGLE: 'translate_single',
  PLAN_CREATE_TO_FIRST_ASSIGN_DURATION: 'plan_create_to_first_assign_duration',
  PLAN_EDIT_TIME_SPENT: 'plan_edit_time_spent',
  PLAN_ASSIGN_TIME_SPENT: 'plan_assign_time_spent',
  CHOOSE_TEAM_MEMBER_MODAL_DURATION: 'choose_team_member_modal_duration',
  PLAN_TASK_BOUNCE: 'plan_task_bounce',
  RESOURCE_NOT_FOUND: 'resource_not_found',
  TEAM_MEMBERS_ASSIGNED: 'team_members_assigned',
  AUTO_ASSIGN_ENABLED: 'auto_assign_enabled',
  PLAN_DESCRIPTION_SAVED: 'plan_description_saved',
  ARCHIVE_PLAN_CLICKED: 'archive_plan_clicked',
  ACTIVATE_PLAN_CLICKED: 'activate_plan_clicked',
  SHOW_TRAIN_TOGETHER_MODE: 'show_train_together_mode',
} as const;

export type AmplitudeProductEventName =
  (typeof AmplitudeProductEvent)[keyof typeof AmplitudeProductEvent];

/**
 * Maps legacy UserAction `action` / track() names to bucket-(b) product events.
 * Omits bucket (a) actions (no product event).
 */
export const LEGACY_ACTION_TO_PRODUCT_EVENT: Partial<
  Record<string, AmplitudeProductEventName>
> = {
  LoginSuccess: AmplitudeProductEvent.LOGIN_SUCCESS,
  color_mode: AmplitudeProductEvent.COLOR_MODE,
  ContentIssue: AmplitudeProductEvent.CONTENT_ISSUE,
  ResourceView: AmplitudeProductEvent.RESOURCE_VIEW,
  category_click: AmplitudeProductEvent.CATEGORY_CLICK,
  subcategory_click: AmplitudeProductEvent.SUBCATEGORY_CLICK,
  searchSuggestionClicked: AmplitudeProductEvent.SEARCH_SUGGESTION_CLICKED,
  document_options: AmplitudeProductEvent.DOCUMENT_OPTIONS,
  document_options_cancel: AmplitudeProductEvent.DOCUMENT_OPTIONS_CANCEL,
  showTrainTogetherMode: AmplitudeProductEvent.SHOW_TRAIN_TOGETHER_MODE,
  searchClick: AmplitudeProductEvent.SEARCH_RESULT_CLICK,
  search_result_click: AmplitudeProductEvent.SEARCH_RESULT_CLICK,
  search_result: AmplitudeProductEvent.SEARCH_RESULT,
  search_time_spent: AmplitudeProductEvent.SEARCH_TIME_SPENT,
  document_print: AmplitudeProductEvent.DOCUMENT_PRINT,
  resource_print: AmplitudeProductEvent.RESOURCE_PRINT,
  certificate_print: AmplitudeProductEvent.CERTIFICATE_PRINT,
  report_print: AmplitudeProductEvent.REPORT_PRINT,
  document_share: AmplitudeProductEvent.DOCUMENT_SHARE,
  document_share_dismissed: AmplitudeProductEvent.DOCUMENT_SHARE_DISMISSED,
  document_switch_language: AmplitudeProductEvent.DOCUMENT_SWITCH_LANGUAGE,
  resource_screenshot: AmplitudeProductEvent.RESOURCE_SCREENSHOT,
  smart_search_toggle: AmplitudeProductEvent.SMART_SEARCH_TOGGLE,
  text_copy: AmplitudeProductEvent.TEXT_COPY,
  text_paste: AmplitudeProductEvent.TEXT_PASTE,
  text_selection: AmplitudeProductEvent.TEXT_SELECTION,
  age_verification_screen_viewed:
    AmplitudeProductEvent.AGE_VERIFICATION_SCREEN_VIEWED,
  age_verification_blocked: AmplitudeProductEvent.AGE_VERIFICATION_BLOCKED,
  'SmartSearch.embeddedReferenceTapped':
    AmplitudeProductEvent.SMART_SEARCH_EMBEDDED_REFERENCE_TAPPED,
  'SmartSearch.referenceImageOpened':
    AmplitudeProductEvent.SMART_SEARCH_REFERENCE_IMAGE_OPENED,
  'SmartSearch.referencePreviewClosed':
    AmplitudeProductEvent.SMART_SEARCH_REFERENCE_PREVIEW_CLOSED,
  'SmartSearch.referenceSourceCardTapped':
    AmplitudeProductEvent.SMART_SEARCH_REFERENCE_SOURCE_CARD_TAPPED,
  quiz_created: AmplitudeProductEvent.QUIZ_CREATED,
  quiz_updated: AmplitudeProductEvent.QUIZ_UPDATED,
  training_plan_created: AmplitudeProductEvent.TRAINING_PLAN_CREATED,
  training_template_copied: AmplitudeProductEvent.TRAINING_TEMPLATE_COPIED,
  translate_all: AmplitudeProductEvent.TRANSLATE_ALL,
  translate_single: AmplitudeProductEvent.TRANSLATE_SINGLE,
  plan_create_to_first_assign_duration:
    AmplitudeProductEvent.PLAN_CREATE_TO_FIRST_ASSIGN_DURATION,
  plan_edit_time_spent: AmplitudeProductEvent.PLAN_EDIT_TIME_SPENT,
  plan_assign_time_spent: AmplitudeProductEvent.PLAN_ASSIGN_TIME_SPENT,
  choose_team_member_modal_duration:
    AmplitudeProductEvent.CHOOSE_TEAM_MEMBER_MODAL_DURATION,
  plan_task_bounce: AmplitudeProductEvent.PLAN_TASK_BOUNCE,
  resource_not_found: AmplitudeProductEvent.RESOURCE_NOT_FOUND,
  team_members_assigned: AmplitudeProductEvent.TEAM_MEMBERS_ASSIGNED,
  auto_assign_enabled: AmplitudeProductEvent.AUTO_ASSIGN_ENABLED,
  plan_description_saved: AmplitudeProductEvent.PLAN_DESCRIPTION_SAVED,
  archive_plan_clicked: AmplitudeProductEvent.ARCHIVE_PLAN_CLICKED,
  activate_plan_clicked: AmplitudeProductEvent.ACTIVATE_PLAN_CLICKED,
};

/** Former top-level native event → product event */
export const LEGACY_TOP_LEVEL_TO_PRODUCT_EVENT: Partial<
  Record<string, AmplitudeProductEventName>
> = {
  ContentIssue: AmplitudeProductEvent.CONTENT_ISSUE,
  LoginSuccess: AmplitudeProductEvent.LOGIN_SUCCESS,
};
